import React, { useState } from 'react';

import { Alert, Box, Button, Container, FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Stack, Typography } from '@mui/material';
import { useAuthUser } from 'react-auth-kit';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';


import { useNavigate } from 'react-router-dom';
import Dashboard from '../../../compounds/Dashboard/Dashboard';
import API_URLS from '../../../../api/api_urls';
import ROUTER_URLS from '../../../utils/router/urls';


function ChangePassword() {
  const navigate = useNavigate();
  const auth = useAuthUser();
  const userInfo = auth();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [errorText, setErrorText ] = useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showOldPassword, setShowOldPassword] = React.useState(false);

  const handleClickShowPassword = () => { setShowPassword((show) => !show) };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleClickShowOldPassword = () => { setShowOldPassword((show) => !show) };

  const handleMouseDownOldPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = (): void => {
    if (newPassword !== confirmPassword) {
      setErrorText("New password and confirmation do not match");
      return;
    }

    if (oldPassword && newPassword && confirmPassword) {
      setIsSaving(true);
      const params = {
        old_password: oldPassword,
        new_password1: newPassword,
        new_password2: confirmPassword,
      }
      axios.post(API_URLS.auth.changePassword, params).then(() => {
        navigate(ROUTER_URLS.dashboard);
      }).catch(() => {
        setIsSaving(false);
        setErrorText("Failed to update password");
      });
    } else {
      setErrorText("Incomplete form")
    }
  };

  const submitEnabled = (): boolean =>
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    newPassword === confirmPassword &&
    Boolean(newPassword.match(/\d/)) &&
    Boolean(newPassword.match(/[a-zA-Z]/));

  if (!userInfo?.id) {
    return (
      <p>Failed to load user record</p>
    )
  }

  return (
    <Dashboard loading={isSaving}>
      <Container component={Stack} maxWidth="md" my={6}>
        <Typography component="h1" variant="h5" my={3}>
            Change Password
        </Typography>

        <Box sx={{ my: 1 }}>
          { errorText &&
            <Alert severity="error" sx={{ my: 2 }}>{ errorText }</Alert>
          }
          <FormControl fullWidth required variant="outlined" sx={{ my: 1 }}>
            <InputLabel htmlFor="outlined-adornment-password">Old Password</InputLabel>
            <OutlinedInput
              id="old-password"
              type={showPassword ? 'text' : 'password'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Old Password"
              name="old-password"
              value={oldPassword}
              onChange={(e) => { setOldPassword(e.target.value) }}
            />
          </FormControl>

          <FormControl fullWidth required variant="outlined" sx={{ my: 1 }}>
            <InputLabel htmlFor="outlined-adornment-password">New Password</InputLabel>
            <OutlinedInput
              id="new-password"
              type={showOldPassword ? 'text' : 'password'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowOldPassword}
                    onMouseDown={handleMouseDownOldPassword}
                    edge="end"
                  >
                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="New Password"
              name="new-password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value) }}
            />
          </FormControl>

          { newPassword.length > 0 && newPassword.length < 8 &&
            <Alert severity="error" sx={{ my: 2 }}>Password must contain at least 8 characters</Alert>
          }
          { newPassword.length > 0 && newPassword.length > 7 && !newPassword.match(/\d/) &&
            <Alert severity="error" sx={{ my: 2 }}>Password must include at least one number</Alert>
          }
          { newPassword.length > 0 && newPassword.length > 7 && !newPassword.match(/[a-zA-Z]/) &&
            <Alert severity="error" sx={{ my: 2 }}>Password must include at least one letter</Alert>
          }

          <FormControl fullWidth required variant="outlined" sx={{ my: 1 }}>
            <InputLabel htmlFor="outlined-adornment-password">Confirm New Password</InputLabel>
            <OutlinedInput
              id="password"
              type={showOldPassword ? 'text' : 'password'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowOldPassword}
                    onMouseDown={handleMouseDownOldPassword}
                    edge="end"
                  >
                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Confirm New Password"
              name="confirm-new-password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value) }}
            />
          </FormControl>

          { newPassword.length > 0 && confirmPassword.length > 0 && newPassword !== confirmPassword &&
            <Alert severity="error" sx={{ my: 2 }}>New password and confirm password do not match</Alert>
          }

          <Button
            type="submit"
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            sx={{ mt: 3, mb: 2 }}
            disabled={!submitEnabled()}
          >
            Reset Password
          </Button>
        </Box>
      </Container>
    </Dashboard>
  );
}

export default ChangePassword;
